import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import apiClient from '../../utils/api';
import { TrendingUp, Package, CreditCard, BarChart2, Download } from 'lucide-react';
import { useToast } from '../../context/ToastContext';

const STATUS_COLORS: Record<string, string> = {
  PENDING: '#f59e0b', CONFIRMED: '#6366f1', DEPARTED: '#0ea5e9',
  IN_TRANSIT: '#3b82f6', DELIVERED: '#22c55e', CANCELLED: '#ef4444', RETURNED: '#94a3b8',
};

export const DispatchReports: React.FC = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [shipments, setShipments] = useState<any[]>([]);
  const [payments, setPayments] = useState<any[]>([]);
  const [transporterStats, setTransporterStats] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const filters: any = {};
      if (dateFrom) filters.dateFrom = dateFrom;
      if (dateTo) filters.dateTo = dateTo;

      const [s, p, t] = await Promise.all([
        apiClient.listShipments(filters, 1000, 0),
        apiClient.listPayments(filters, 1000, 0),
        apiClient.getTransporterStats(),
      ]);
      const sd = s.data.data; setShipments(sd?.shipments ?? sd?.rows ?? (Array.isArray(sd) ? sd : []));
      const pd = p.data.data; setPayments(pd?.payments ?? pd?.rows ?? (Array.isArray(pd) ? pd : []));
      setTransporterStats(t.data.data ?? []);
    } catch {} finally { setLoading(false); }
  }, [dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const filters: any = {};
      if (dateFrom) filters.dateFrom = dateFrom;
      if (dateTo) filters.dateTo = dateTo;
      const res = await apiClient.exportShipmentsCsv(filters);
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `dispatch-report-${Date.now()}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast('Report exported', 'success');
    } catch { toast('Export failed', 'error'); }
    finally { setExporting(false); }
  };

  const byStatus = Object.entries(
    shipments.reduce((acc: any, s) => { acc[s.status] = (acc[s.status] || 0) + 1; return acc; }, {})
  ).sort((a: any, b: any) => b[1] - a[1]);

  const deliveryRate = shipments.length > 0
    ? Math.round((shipments.filter(s => s.status === 'DELIVERED').length / shipments.length) * 100) : 0;

  const totalRevenue = payments.filter(p => p.status === 'COMPLETED').reduce((s, p) => s + Number(p.amount), 0);
  const pendingRevenue = payments.filter(p => p.status === 'PENDING').reduce((s, p) => s + Number(p.amount), 0);

  const destinations = Object.entries(
    shipments.reduce((acc: any, s) => {
      const city = s.receiverCity || 'Unknown';
      acc[city] = (acc[city] || 0) + 1;
      return acc;
    }, {})
  ).sort((a: any, b: any) => b[1] - a[1]).slice(0, 5);

  const now = new Date();
  const months = Array.from({ length: 6 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1);
    return { label: d.toLocaleString('default', { month: 'short' }), year: d.getFullYear(), month: d.getMonth() };
  });
  const monthlyShipments = months.map(m => ({
    label: m.label,
    count: shipments.filter(s => {
      const d = new Date(s.createdAt);
      return d.getFullYear() === m.year && d.getMonth() === m.month;
    }).length,
  }));
  const maxMonthly = Math.max(...monthlyShipments.map(m => m.count), 1);

  if (loading) return <div className="loading-spinner">Loading reports...</div>;

  return (
    <>
      <div className="page-header">
        <h1 className="page-title">Reports & Analytics</h1>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} title="From date" style={{ width: 145 }} />
          <span style={{ color: '#94a3b8', fontSize: 13 }}>to</span>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} title="To date" style={{ width: 145 }} />
          <button className="btn btn-ghost" onClick={exportCsv} disabled={exporting}>
            <Download size={15} /> {exporting ? 'Exporting...' : 'Export CSV'}
          </button>
        </div>
      </div>

      <div className="stats-grid" style={{ marginBottom: 20 }}>
        {[
          { label: 'Total Shipments', value: shipments.length, icon: <Package size={20} />, color: '#3b82f6' },
          { label: 'Delivery Rate', value: `${deliveryRate}%`, icon: <TrendingUp size={20} />, color: '#22c55e' },
          { label: 'Revenue Collected', value: `${totalRevenue.toLocaleString()} RWF`, icon: <CreditCard size={20} />, color: '#6366f1' },
          { label: 'Pending Revenue', value: `${pendingRevenue.toLocaleString()} RWF`, icon: <BarChart2 size={20} />, color: '#f59e0b' },
        ].map(s => (
          <div key={s.label} className="stat-card" style={{ borderLeft: `4px solid ${s.color}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <div>
                <div className="stat-label">{s.label}</div>
                <div className="stat-value" style={{ color: s.color, fontSize: typeof s.value === 'string' && s.value.length > 8 ? 18 : 28 }}>{s.value}</div>
              </div>
              <div style={{ color: s.color, opacity: 0.6 }}>{s.icon}</div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
        <div className="card">
          <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>Shipments by Status</h2>
          {byStatus.length === 0 ? <div style={{ color: '#94a3b8', fontSize: 14 }}>No data yet.</div> : byStatus.map(([status, count]: any) => (
            <div key={status} style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 13 }}>
                <span style={{ fontWeight: 500 }}>{status.replace(/_/g, ' ')}</span>
                <span style={{ color: '#64748b' }}>{count} ({shipments.length > 0 ? Math.round((count / shipments.length) * 100) : 0}%)</span>
              </div>
              <div style={{ height: 8, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${shipments.length > 0 ? (count / shipments.length) * 100 : 0}%`, background: STATUS_COLORS[status] || '#94a3b8', borderRadius: 4, transition: 'width 0.6s ease' }} />
              </div>
            </div>
          ))}
        </div>

        <div className="card">
          <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>Monthly Shipments (Last 6 Months)</h2>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 120 }}>
            {monthlyShipments.map(m => (
              <div key={m.label} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>{m.count}</div>
                <div style={{ width: '100%', height: `${(m.count / maxMonthly) * 90}px`, minHeight: m.count > 0 ? 4 : 0, background: 'linear-gradient(180deg, #3b82f6, #6366f1)', borderRadius: '4px 4px 0 0', transition: 'height 0.6s ease' }} />
                <div style={{ fontSize: 11, color: '#94a3b8' }}>{m.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
        <div className="card">
          <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>Top Destinations</h2>
          {destinations.length === 0 ? (
            <div style={{ color: '#94a3b8', fontSize: 14 }}>No data yet.</div>
          ) : destinations.map(([city, count]: any, i) => (
            <div key={city} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
              <div style={{ width: 24, height: 24, borderRadius: '50%', background: '#eff6ff', color: '#3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>{i + 1}</div>
              <div style={{ flex: 1, fontSize: 14, fontWeight: 500 }}>{city}</div>
              <div style={{ fontSize: 13, color: '#64748b' }}>{count} shipments</div>
            </div>
          ))}
        </div>

        <div className="card">
          <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>Transporter Performance</h2>
          {transporterStats.length === 0 ? (
            <div style={{ color: '#94a3b8', fontSize: 14 }}>No transporter data yet.</div>
          ) : (
            <div className="table-wrapper">
              <table>
                <thead><tr><th>Transporter</th><th>Total</th><th>Delivered</th><th>Rate</th></tr></thead>
                <tbody>
                  {transporterStats.slice(0, 8).map((t: any) => (
                    <tr key={t.transporter.id}>
                      <td style={{ fontWeight: 500 }}>{t.transporter.name}</td>
                      <td>{t.total}</td>
                      <td>{t.delivered}</td>
                      <td><span style={{ color: t.successRate >= 80 ? '#22c55e' : '#f59e0b', fontWeight: 600 }}>{t.successRate}%</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
